import { useEffect, useState } from "react";
import { Cloud, Loader2, Smartphone, X } from "lucide-react";

export type SimpleDocSaveDestination = "phone" | "nevisign";

type Props = {
  open: boolean;
  initialTitle: string;
  busy?: boolean;
  error?: string | null;
  dialogLabel?: string;
  phoneLabel?: string;
  phoneHint?: string;
  nevisignLabel?: string;
  nevisignHint?: string;
  onClose: () => void;
  onConfirm: (opts: {
    title: string;
    destination: SimpleDocSaveDestination;
  }) => void;
};

export function SimpleDocSaveModal({
  open,
  initialTitle,
  busy = false,
  error = null,
  dialogLabel = "ذخیره سند",
  phoneLabel = "ذخیره در گوشی",
  phoneHint = "خروجی Word با پسوند .docx",
  nevisignLabel = "ذخیره در نویساین",
  nevisignHint = "در حساب کاربری (در صورت نیاز ورود)",
  onClose,
  onConfirm,
}: Props) {
  const [title, setTitle] = useState(initialTitle);
  const [destination, setDestination] =
    useState<SimpleDocSaveDestination>("phone");

  useEffect(() => {
    if (!open) return;
    setTitle(initialTitle);
    setDestination("phone");
  }, [open, initialTitle]);

  if (!open) return null;

  const canSubmit = Boolean(title.trim()) && !busy;

  return (
    <div
      className="sheet-backdrop"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="sheet-card simple-doc-save-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sheet-head">
          <h2>{dialogLabel}</h2>
          <button
            type="button"
            className="icon-btn"
            aria-label="بستن"
            disabled={busy}
            onClick={onClose}
          >
            <X size={16} />
          </button>
        </header>

        <label className="simple-doc-save-field">
          <span>عنوان سند</span>
          <input
            type="text"
            value={title}
            disabled={busy}
            autoFocus
            dir="auto"
            placeholder="سند بدون عنوان"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>

        <fieldset className="simple-doc-save-dest" disabled={busy}>
          <legend>محل ذخیره</legend>
          <label
            className={`simple-doc-save-option${destination === "phone" ? " is-selected" : ""}`}
          >
            <input
              type="radio"
              name="simple-doc-dest"
              checked={destination === "phone"}
              onChange={() => setDestination("phone")}
            />
            <Smartphone size={18} aria-hidden />
            <span>
              <strong>{phoneLabel}</strong>
              <em>{phoneHint}</em>
            </span>
          </label>
          <label
            className={`simple-doc-save-option${destination === "nevisign" ? " is-selected" : ""}`}
          >
            <input
              type="radio"
              name="simple-doc-dest"
              checked={destination === "nevisign"}
              onChange={() => setDestination("nevisign")}
            />
            <Cloud size={18} aria-hidden />
            <span>
              <strong>{nevisignLabel}</strong>
              <em>{nevisignHint}</em>
            </span>
          </label>
        </fieldset>

        {error ? <p className="reader-login-error">{error}</p> : null}

        <div className="sheet-actions sticky-actions">
          <button
            type="button"
            className="ghost-btn"
            disabled={busy}
            onClick={onClose}
          >
            انصراف
          </button>
          <button
            type="button"
            className="primary-btn"
            disabled={!canSubmit}
            onClick={() =>
              onConfirm({
                title: title.trim() || "سند بدون عنوان",
                destination,
              })
            }
          >
            {busy ? <Loader2 size={16} className="spin" /> : null}
            {busy ? "در حال ذخیره…" : "تأیید و ذخیره"}
          </button>
        </div>
      </div>
    </div>
  );
}
